import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  Row,
  Col,
  Divider,
  Typography,
  Upload
} from "antd";
import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";

import {
  REWARD_KIT_ITEM_KEYS_ENUM,
  REWARD_KIT_ITEM_IMAGE_MIME_TYPES,
  convertRewardKitItemsMapToFormList,
  convertRewardKitItemsFormListToMap
} from "./helper";
import { selectFormOptionsNoFormatting } from "../../../utils/helper";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";

/** Whole number of at least 1, so a kit's coin threshold can never be zero. */
const POSITIVE_WHOLE_NUMBER_PATTERN = /^[1-9]\d*$/;

const CreateCourseModal = ({
  isModalOpen = false,
  isEdit = false,
  courseTypes = [],
  batchesInfo = [],
  coursesList = [],
  saleStatusesList = [],
  initialValues = {},
  ROOT_URL,
  setIsModalOpen = () => {},
  handleCreateEditCourse = () => {}
}) => {
  const [form] = Form.useForm();
  const { config: adminConfig } = useSelector((state) => state?.reducer?.authInfo) || {};
  const hasRewardKitManageAccess = !!adminConfig?.courses?.hasRewardKitManageAccess;
  const [uploadingRewardKitItemImageIndex, setUploadingRewardKitItemImageIndex] = useState(null);
  const isRewardKitEnabled = Form.useWatch(["rewardKitMetadata", "isEnabled"], form);
  const rewardKitItemsWatch = Form.useWatch(["rewardKitMetadata", "items"], form) || [];

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  /**
   * Submits the course form. The reward kit is part of the payload only for an admin with
   * `courses.hasRewardKitManageAccess`, so any other admin leaves the stored kit untouched.
   *
   * @param {object} values - The validated form values.
   */
  const handleFormSubmit = (values) => {
    const { config: valuesConfig, rewardKitMetadata: valuesRewardKitMetadata, ...otherValues } = values;
    // Merge initial config/rewardKitMetadata with submitted values to preserve hidden
    // backend fields; items also needs the Form.List -> Map conversion (see helper.jsx).
    const mergedValues = {
      ...otherValues,
      config: {
        ...(isEdit ? initialValues.config : {}),
        ...valuesConfig
      }
    };
    if (hasRewardKitManageAccess) {
      mergedValues.rewardKitMetadata = {
        ...(isEdit ? initialValues.rewardKitMetadata : {}),
        ...valuesRewardKitMetadata,
        items: convertRewardKitItemsFormListToMap(valuesRewardKitMetadata?.items)
      };
    }
    handleCreateEditCourse(mergedValues);
  };

  /**
   * Uploads a reward-kit item's display image and writes the hosted URL into
   * the matching `Form.List` row; returns false to block AntD's auto-upload.
   *
   * @param {File} file - The image chosen in the upload control.
   * @param {number} rewardKitItemFieldIndex - Index of the `Form.List` row that receives the URL.
   * @returns {boolean} Always `false`.
   */
  const uploadRewardKitItemDisplayImage = (file, rewardKitItemFieldIndex) => {
    setUploadingRewardKitItemImageIndex(rewardKitItemFieldIndex);
    const formData = new FormData();
    formData.append("file", file);

    axios
      .post(`${ROOT_URL}/admin/courses/reward-item-upload-asset`, formData)
      .then((uploadResponse) => {
        const uploadedImageUrl = uploadResponse?.data?.url;
        const currentRewardKitItemsList = form.getFieldValue(["rewardKitMetadata", "items"]) || [];
        const updatedRewardKitItemsList = currentRewardKitItemsList.map(
          (rewardKitItem, rewardKitItemIndex) =>
            rewardKitItemIndex === rewardKitItemFieldIndex
              ? { ...rewardKitItem, displayImageUrl: uploadedImageUrl }
              : rewardKitItem
        );
        form.setFieldValue(["rewardKitMetadata", "items"], updatedRewardKitItemsList);
        showSuccessNotification("Reward kit item image uploaded successfully.");
      })
      .catch(showErrorNotification)
      .finally(() => {
        setUploadingRewardKitItemImageIndex(null);
      });

    return false;
  };

  const { rewardKitMetadata: initialRewardKitMetadata } = initialValues || {};
  const formInitialValues = isEdit
    ? {
      ...initialValues,
      rewardKitMetadata: {
        ...initialRewardKitMetadata,
        items: convertRewardKitItemsMapToFormList(initialRewardKitMetadata?.items)
      }
    }
    : {};

  const rewardKitItemKeyOptions = selectFormOptionsNoFormatting(
    Object.values(REWARD_KIT_ITEM_KEYS_ENUM)
  );

  const { Item: FormItem } = Form;

  return (
    <Modal
      title={isEdit ? "Edit Course" : "Create Course"}
      open={isModalOpen}
      footer={[]}
      onOk={handleOk}
      width="min(1000px, calc(100vw - 32px))"
      onCancel={handleCancel}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={formInitialValues}
        name="new_course_form"
        onFinish={handleFormSubmit}
      >
        <Divider orientation="left">Basic Details</Divider>
        {!isEdit && (
          <>
            <FormItem
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  message: "Please enter the course name!"
                }
              ]}
            >
              <Input placeholder="Some Cool Name!" disabled={isEdit} />
            </FormItem>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <FormItem
                  name="type"
                  label="Type"
                  rules={[
                    {
                      required: true,
                      message: "Please select a course type!"
                    }
                  ]}
                >
                  <Select
                    options={selectFormOptionsNoFormatting(courseTypes || [])}
                    showSearch
                    allowClear
                    disabled={isEdit}
                  />
                </FormItem>
              </Col>
              <Col xs={24} md={12}>
                <FormItem
                  name="batch"
                  label="Batch"
                  rules={[
                    {
                      required: true,
                      message: "Please select a batch!"
                    }
                  ]}
                >
                  <Select options={batchesInfo || []} showSearch disabled={isEdit} allowClear />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <FormItem
                  name="displayName"
                  label="Display Name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the course name!"
                    }
                  ]}
                >
                  <Input placeholder="This will be visible on the My Learnings dropdown" />
                </FormItem>
              </Col>
              <Col xs={24} md={12}>
                <FormItem
                  name="uiDisplayName"
                  label="UI Display Name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the UI Display Name"
                    }
                  ]}
                >
                  <Input placeholder="" />
                </FormItem>
              </Col>
            </Row>
          </>
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormItem
              name="urlIdentifier"
              label="URL Identifier"
              rules={[
                {
                  required: true,
                  message: "Please enter the URL Identifier"
                }
              ]}
            >
              <Input placeholder="" />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem name="descendantCourseId" label="Descendant Course">
              <Select options={coursesList || []} showSearch allowClear />
            </FormItem>
          </Col>
        </Row>

        <FormItem name="whatsappCommunityURL" label="Whatsapp URL">
          <Input placeholder="Whatsapp group link of the course" />
        </FormItem>

        <Divider orientation="left">Pricing & Capacity</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormItem
              name="price"
              label="Price"
              rules={[
                {
                  required: true,
                  message: "Please enter a price"
                },
                {
                  pattern: /[\d,.]+/,
                  message: "Please enter a valid number."
                }
              ]}
            >
              <Input placeholder="10000" />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem
              name="doubtSupportExtensionPrice"
              label="Doubt Support Extension Price"
              rules={[
                {
                  pattern: /[\d,.]+/,
                  message: "Please enter a valid number."
                }
              ]}
            >
              <Input placeholder="500" />
            </FormItem>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormItem
              name="slotCap"
              label="Slot Capacity"
              rules={[
                {
                  required: true,
                  message: "Please enter a valid slot capacity"
                },
                {
                  pattern: /[\d]+/,
                  message: "Please enter a valid number."
                }
              ]}
            >
              <Input placeholder="1000" />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem
              name="saleStatus"
              label="Sale Status"
              rules={[
                {
                  required: true,
                  message: "Please select a sale status!"
                }
              ]}
            >
              <Select
                options={selectFormOptionsNoFormatting(saleStatusesList || [])}
                showSearch
                allowClear
              />
            </FormItem>
          </Col>
        </Row>

        <Divider orientation="left">Validity Info</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormItem
              name={["validityInfo", "contentValidityMonths"]}
              label="Content Validity (Months)"
              rules={[
                {
                  required: true,
                  message: "Please enter content validity in months"
                },
                {
                  pattern: /^[\d]+$/,
                  message: "Please enter a valid number."
                }
              ]}
            >
              <Input placeholder="12" />
            </FormItem>
          </Col>
        </Row>

        <Divider orientation="left">Settings</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <FormItem
              name={["config", "isModuleBasedContentManagementSupported"]}
              label="Module Based Content Management Supported"
              valuePropName="checked"
            >
              <Switch />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem
              name={["config", "aiDoubtSupportCreditLimit"]}
              label="AI Doubt Support Credits per User"
              rules={[
                {
                  pattern: /[\d,.]+/,
                  message: "Please enter a valid number."
                }
              ]}
            >
              <Input placeholder="100" />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              name={["config", "guidanceSessionsLimit"]}
              label="Guidance Sessions Limit"
              rules={[
                {
                  pattern: /[\d]+/,
                  message: "Please enter a valid number."
                }
              ]}
            >
              <Input placeholder="0" />
            </FormItem>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <FormItem name="feedbackEnabled" label="Course Feedback" valuePropName="checked">
              <Switch />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              name="certificateDownloadEnabled"
              label="Certificate download"
              valuePropName="checked"
            >
              <Switch />
            </FormItem>
          </Col>
          <Col xs={24} md={12}>
            <FormItem
              name="leaderboardFreezeEnabled"
              label="Freeze leaderboard"
              valuePropName="checked"
            >
              <Switch />
            </FormItem>
          </Col>
        </Row>

        <Divider orientation="left">Reward Kit</Divider>
        {!hasRewardKitManageAccess && (
          <Typography.Paragraph type="secondary">
          Changing the reward kit needs reward kit manage access. These fields are read-only.
          </Typography.Paragraph>
        )}
        <ConfigProvider componentDisabled={!hasRewardKitManageAccess}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <FormItem
                name={["rewardKitMetadata", "isEnabled"]}
                label="Reward Kit Enabled"
                valuePropName="checked"
              >
                <Switch />
              </FormItem>
            </Col>
            <Col xs={24} md={8}>
              <FormItem
                name={["rewardKitMetadata", "rewardCoinThreshold"]}
                label="Reward Kit Coin Threshold"
                rules={[
                  {
                    required: !!isRewardKitEnabled,
                    message: "Required while the reward kit is enabled"
                  },
                  {
                    pattern: POSITIVE_WHOLE_NUMBER_PATTERN,
                    message: "Please enter a whole number greater than 0."
                  }
                ]}
              >
                <Input
                  placeholder="7500"
                  onChange={() => form.validateFields([["rewardKitMetadata", "items"]])}
                />
              </FormItem>
            </Col>
          </Row>

          <Form.List
            name={["rewardKitMetadata", "items"]}
            rules={[
              {
                validator: async (_, rewardKitItemsListValue) => {
                  const selectedItemKeys = (rewardKitItemsListValue || [])
                    .map((rewardKitItem) => rewardKitItem?.itemKey)
                    .filter(Boolean);
                  const uniqueSelectedItemKeys = new Set(selectedItemKeys);
                  if (uniqueSelectedItemKeys.size !== selectedItemKeys.length) {
                    throw new Error(
                      "Each reward kit item key must be unique. Remove or change the duplicate."
                    );
                  }
                  if (isRewardKitEnabled && selectedItemKeys.length === 0) {
                    throw new Error(
                      "Add at least one reward kit item before enabling the reward kit."
                    );
                  }
                  // The backend checks the sum whenever the kit has items, even when it is disabled.
                  if (selectedItemKeys.length) {
                    const coinPoolTotal = Number(
                      form.getFieldValue(["rewardKitMetadata", "rewardCoinThreshold"])
                    ) || 0;
                    const totalItemCoinCost = (rewardKitItemsListValue || []).reduce(
                      (runningTotal, rewardKitItem) =>
                        runningTotal + (Number(rewardKitItem?.rewardCoinThreshold) || 0),
                      0
                    );
                    if (totalItemCoinCost !== coinPoolTotal) {
                      throw new Error(
                        `Item costs must sum to exactly the reward kit coin threshold (${coinPoolTotal}), but they currently sum to ${totalItemCoinCost}.`
                      );
                    }
                  }
                }
              }
            ]}
          >
            {(rewardKitItemFields, { add: addRewardKitItem, remove: removeRewardKitItem }, { errors: rewardKitItemListErrors }) => (
              <>
                {rewardKitItemFields.map((rewardKitItemField) => {
                  const { key: rewardKitItemFieldKey, name: rewardKitItemFieldName } = rewardKitItemField;
                  const itemKeysUsedByOtherRows = rewardKitItemsWatch
                    .filter((_, rewardKitItemRowIndex) => rewardKitItemRowIndex !== rewardKitItemFieldName)
                    .map((rewardKitItem) => rewardKitItem?.itemKey)
                    .filter(Boolean);
                  const availableRewardKitItemKeyOptions = rewardKitItemKeyOptions.filter(
                    (rewardKitItemKeyOption) => !itemKeysUsedByOtherRows.includes(rewardKitItemKeyOption.value)
                  );

                  return (
                    <Row gutter={16} key={rewardKitItemFieldKey} align="middle">
                      <Col xs={24} md={5}>
                        <FormItem
                          name={[rewardKitItemFieldName, "itemKey"]}
                          label="Item Key"
                          rules={[{ required: true, message: "Please select an item key" }]}
                        >
                          <Select options={availableRewardKitItemKeyOptions} showSearch allowClear />
                        </FormItem>
                      </Col>
                      <Col xs={24} md={5}>
                        <FormItem
                          name={[rewardKitItemFieldName, "displayName"]}
                          label="Display Name"
                          rules={[{ required: true, message: "Please enter a display name" }]}
                        >
                          <Input placeholder="TLE Brand Pen" />
                        </FormItem>
                      </Col>
                      <Col xs={12} md={3}>
                        <FormItem
                          name={[rewardKitItemFieldName, "rewardCoinThreshold"]}
                          label="Coin Cost"
                          rules={[
                            { required: true, message: "Please enter a threshold" },
                            { pattern: /^[\d]+$/, message: "Please enter a valid number." }
                          ]}
                        >
                          <Input placeholder="500" />
                        </FormItem>
                      </Col>
                      <Col xs={24} md={6}>
                        <FormItem
                          name={[rewardKitItemFieldName, "displayImageUrl"]}
                          label="Display Image URL"
                          rules={[{ required: true, message: "Please upload a display image" }]}
                        >
                          <Input placeholder="Uploaded image URL will appear here" readOnly />
                        </FormItem>
                      </Col>
                      <Col xs={18} md={3}>
                        <FormItem label=" ">
                          <Upload
                            beforeUpload={(file) =>
                              uploadRewardKitItemDisplayImage(file, rewardKitItemFieldName)
                            }
                            multiple={false}
                            showUploadList={false}
                            accept={REWARD_KIT_ITEM_IMAGE_MIME_TYPES.join(",")}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              loading={uploadingRewardKitItemImageIndex === rewardKitItemFieldName}
                              block
                            >
                          Upload
                            </Button>
                          </Upload>
                        </FormItem>
                      </Col>
                      <Col xs={6} md={2}>
                        <FormItem label=" ">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeRewardKitItem(rewardKitItemFieldName)}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  );
                })}
                <Form.ErrorList errors={rewardKitItemListErrors} />
                <FormItem>
                  <Button
                    type="dashed"
                    onClick={() => addRewardKitItem()}
                    disabled={
                      !hasRewardKitManageAccess ||
                    rewardKitItemFields.length >= Object.keys(REWARD_KIT_ITEM_KEYS_ENUM).length
                    }
                    block
                  >
                Add Reward Kit Item
                  </Button>
                </FormItem>
              </>
            )}
          </Form.List>
        </ConfigProvider>

        <FormItem className="submit-btn" style={{ marginTop: 24, textAlign: "right" }}>
          <Button type="primary" htmlType="submit">
            {isEdit ? "Update" : "Create"}
          </Button>
        </FormItem>
      </Form>
    </Modal>
  );
};
export default CreateCourseModal;
